import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Users, CheckCircle } from "@phosphor-icons/react";
import api from "@/lib/api";
import PageMeta from "@/components/PageMeta";

// No-login invite page opened from the Family gift reminder email.
export default function FamilyEmailInvite() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [form, setForm] = useState({ name: "", email: "" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/family-plan/email-invite/${token}`)
    .then((r) => setInfo(r.data))
    .catch((e) => setErr(e?.response?.data?.detail || "This invite link isn't valid."));
  useEffect(() => { load(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/family-plan/email-invite/${token}/invites`, form);
      toast.success(`Invite sent to ${form.name}`);
      setForm({ name: "", email: "" });
      await load();
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Couldn't send that invite");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-xl mx-auto px-6 py-16" data-testid="family-email-invite-page">
      <PageMeta title="Invite your family to Recipe Filer" noindex />
      <Users size={40} weight="duotone" className="text-rf-orange mb-3" />
      {err ? (
        <p className="text-rf-ink text-lg" data-testid="family-email-invite-error">{err}</p>
      ) : !info ? (
        <p className="text-rf-ink-soft">Loading…</p>
      ) : (
        <>
          <h1 className="font-display text-4xl font-black text-rf-ink mb-3">Hi {info.owner_name}, who&apos;s joining you?</h1>
          <p className="text-rf-ink-soft mb-6" data-testid="family-email-invite-slots">
            Your Family Plan has <b>{info.slots_left}</b> free place{info.slots_left === 1 ? "" : "s"} left. Add a name and email and we&apos;ll send them their invitation — no need to sign in.
          </p>
          {info.invites.length > 0 && (
            <ul className="mb-6 space-y-2" data-testid="family-email-invite-list">
              {info.invites.map((i) => (
                <li key={i.invitee_name + i.status} className="flex items-center gap-2 text-sm text-rf-ink">
                  <CheckCircle size={18} weight="fill" className="text-rf-orange" />
                  {i.invitee_name} <span className="text-rf-ink-soft">· {i.status === "accepted" ? "joined" : "invited"}</span>
                </li>
              ))}
            </ul>
          )}
          {info.slots_left > 0 ? (
            <form onSubmit={submit} className="card-recipe space-y-4">
              <div>
                <label className="overline">Their name</label>
                <input data-testid="family-email-invite-name" required value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-rf mt-1" placeholder="e.g. Grandma Jean" />
              </div>
              <div>
                <label className="overline">Their email</label>
                <input data-testid="family-email-invite-email" required type="email" value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })} className="input-rf mt-1" placeholder="jean@example.com" />
              </div>
              <button data-testid="family-email-invite-submit" type="submit" disabled={busy} className="btn-amber w-full">
                {busy ? "Sending…" : "Send invitation"}
              </button>
            </form>
          ) : (
            <p className="text-rf-ink font-semibold" data-testid="family-email-invite-full">Your plan is full — everyone&apos;s at the table.</p>
          )}
        </>
      )}
    </div>
  );
}
